import { useCallback, useEffect, useRef, useState } from "react";
import { MessageCircle, Trash2, Loader2, Pencil, Search, X } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { api, errMsg } from "@/lib/api";

const fmt = (d) => new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const Highlight = ({ text, q }) => {
  if (!q) return text;
  const parts = text.split(new RegExp(`(${esc(q)})`, "gi"));
  let at = 0;
  return parts.map((p, i) => { const key = `${at}-${p}`; at += p.length; return i % 2 ? <mark key={key} className="bg-mm-primary/20 text-inherit rounded px-0.5">{p}</mark> : <span key={key}>{p}</span>; });
};

const RenameForm = ({ c, onSave, onCancel }) => {
  const [t, setT] = useState(c.custom_title ? c.title : "");
  const [busy, setBusy] = useState(false);
  const save = async (e) => { e.preventDefault(); setBusy(true); const ok = await onSave(c.session_id, t.trim()); setBusy(false); if (ok) onCancel(); };
  return (
    <form onSubmit={save} className="flex-1 min-w-0" data-testid={`ai-past-chat-rename-form-${c.session_id}`}>
      <input autoFocus className="mm-input !py-1.5 text-sm" maxLength={60} placeholder={c.custom_title ? "Clear to use the automatic title" : c.title}
        value={t} onChange={(e) => setT(e.target.value)} data-testid={`ai-past-chat-rename-input-${c.session_id}`} />
      <div className="flex items-center gap-3 mt-1.5 text-xs">
        <button className="text-mm-primary font-semibold" disabled={busy} data-testid={`ai-past-chat-rename-save-${c.session_id}`}>Save</button>
        <button type="button" className="text-mm-muted" onClick={onCancel} data-testid={`ai-past-chat-rename-cancel-${c.session_id}`}>Cancel</button>
        <span className="ml-auto text-mm-muted">{t.length}/60</span>
      </div>
    </form>
  );
};

const RowActions = ({ c, onRename, onDelete }) => {
  const [armed, setArmed] = useState(false);
  if (armed) return (
    <div className="flex flex-col items-end gap-1 text-xs shrink-0">
      <button onClick={() => onDelete(c.session_id)} className="text-mm-error font-semibold" data-testid={`ai-past-chat-delete-yes-${c.session_id}`}>Delete</button>
      <button onClick={() => setArmed(false)} className="text-mm-muted" data-testid={`ai-past-chat-delete-cancel-${c.session_id}`}>Cancel</button>
    </div>
  );
  return (
    <div className="flex shrink-0">
      <button onClick={onRename} className="p-1.5 text-mm-muted hover:text-mm-primary transition-colors" aria-label="Rename chat" data-testid={`ai-past-chat-rename-${c.session_id}`}><Pencil size={15} /></button>
      <button onClick={() => setArmed(true)} className="p-1.5 text-mm-muted hover:text-mm-error transition-colors" aria-label="Delete chat" data-testid={`ai-past-chat-delete-${c.session_id}`}><Trash2 size={15} /></button>
    </div>
  );
};

const ChatRow = ({ c, q, active, onOpen, onDelete, onRename }) => {
  const [editing, setEditing] = useState(false);
  return (
    <div className={`mm-card p-3 flex items-start gap-2 ${active ? "ring-2 ring-mm-primary" : ""}`} data-testid={`ai-past-chat-${c.session_id}`}>
      {editing ? <RenameForm c={c} onSave={onRename} onCancel={() => setEditing(false)} /> : (
        <button onClick={() => onOpen(c.session_id)} className="flex-1 min-w-0 text-left break-words [overflow-wrap:anywhere]" data-testid={`ai-past-chat-open-${c.session_id}`}>
          <div className="font-medium text-sm"><Highlight text={c.title} q={q} /></div>
          {c.snippet && <div className="text-xs mt-1 mm-soft rounded-lg px-2 py-1" data-testid={`ai-past-chat-snippet-${c.session_id}`}><Highlight text={c.snippet} q={q} /></div>}
          <div className="text-xs text-mm-muted mt-1">{fmt(c.updated_at)} · {c.count} message{c.count === 1 ? "" : "s"}{active ? " · open now" : ""}</div>
        </button>
      )}
      {!editing && <RowActions c={c} onRename={() => setEditing(true)} onDelete={onDelete} />}
    </div>
  );
};

const SearchBox = ({ q, setQ }) => (
  <div className="relative mt-4">
    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-mm-muted pointer-events-none" />
    <input className="mm-input !pl-9 !pr-9 text-sm" placeholder="Search your chats" value={q} maxLength={100} onChange={(e) => setQ(e.target.value)} aria-label="Search chats" data-testid="ai-past-chats-search" />
    {q && <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-mm-muted" aria-label="Clear search" data-testid="ai-past-chats-search-clear"><X size={15} /></button>}
  </div>
);

// Saved Mia conversations (synced to the account): search, rename, reread/continue, delete
export const PastChats = ({ open, onOpenChange, activeId, onPick, onDeleted }) => {
  const [chats, setChats] = useState(null);
  const [q, setQ] = useState("");
  const term = q.trim();
  const reqId = useRef(0);
  const termRef = useRef(term);
  termRef.current = term;

  // Only the latest request may update the list (avoids stale search results)
  const load = useCallback((t) => {
    const id = ++reqId.current;
    return api.get("/ai/chats", { params: t ? { q: t } : {} })
      .then((r) => { if (id === reqId.current) setChats(r.data); })
      .catch((e) => { if (id === reqId.current) { toast.error(errMsg(e)); setChats((l) => l || []); } });
  }, []);

  useEffect(() => { if (!open) setQ(""); }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    setChats(null);
    reqId.current += 1;
    const t = setTimeout(() => load(term), term ? 300 : 0);
    return () => clearTimeout(t);
  }, [open, term, load]);

  const del = async (id) => {
    try {
      await api.delete(`/ai/chat-history/${id}`);
      setChats((l) => l.filter((c) => c.session_id !== id));
      onDeleted(id);
      toast.success("Chat deleted");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const rename = async (id, title) => {
    try { await api.put(`/ai/chats/${id}/title`, { title }); }
    catch (e) { toast.error(errMsg(e)); return false; }
    toast.success(title ? "Chat renamed" : "Back to the automatic title");
    load(termRef.current);
    return true;
  };

  const empty = term ? `No chats mention “${term}”.` : "No past chats yet. They'll show up here after you talk with Mia.";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="ai-past-chats">
        <SheetHeader>
          <SheetTitle>Past chats</SheetTitle>
          <SheetDescription>Your conversations with Mia, saved to your account. Tap one to reread or continue it.</SheetDescription>
        </SheetHeader>
        <SearchBox q={q} setQ={setQ} />
        <div className="mt-4 space-y-2">
          {chats === null && <div className="flex justify-center py-8"><Loader2 className="animate-spin text-mm-primary" /></div>}
          {chats?.length === 0 && (
            <div className="text-center py-10 text-sm text-mm-muted" data-testid="ai-past-chats-empty"><MessageCircle className="mx-auto mb-2 text-mm-primary" />{empty}</div>
          )}
          {chats?.map((c) => <ChatRow key={c.session_id} c={c} q={term} active={c.session_id === activeId} onOpen={onPick} onDelete={del} onRename={rename} />)}
        </div>
      </SheetContent>
    </Sheet>
  );
};
